import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { RefreshCw, Radio, ShieldAlert, TrendingUp, Building2, Maximize2 } from "lucide-react";

/**
 * Executive Situation Room — /admin/situation-room
 * 1-hour rolling window across every asset class + 24h risk feed.
 * Polls every 60 seconds; fullscreen presentation mode via browser API.
 */
export default function SituationRoom() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/executive/situation");
      setData(r.data);
    } catch {} finally { setBusy(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-6 py-10">
        <header className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="kicker flex items-center gap-2"><Radio className="h-3 w-3 text-primary animate-pulse" /> LIVE · Executive Situation Room</div>
            <h1 className="display text-4xl">THOM Verify™ Africa — Live</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              Rolling 60-minute activity window. Refreshes automatically every 60 seconds. Includes verification requests, active escrows, high-value transactions and the 24-hour risk feed.
            </p>
            {data?.as_of && <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground mt-2">As of {data.as_of.slice(0, 19).replace("T", " ")} UTC</div>}
          </div>
          <div className="flex gap-2 items-center">
            <Button size="sm" variant="outline" onClick={load} disabled={busy}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            <Button size="sm" variant="outline" onClick={goFullscreen} data-testid="sitroom-fullscreen"><Maximize2 className="h-3.5 w-3.5 mr-1" /> Fullscreen</Button>
            <Link to="/admin/intelligence"><Button size="sm">Intelligence Centre</Button></Link>
          </div>
        </header>

        <div className="grid lg:grid-cols-3 gap-4 mb-6" data-testid="sitroom-strip">
          <StripCard icon={TrendingUp} label="Live requests · last hour"   value={(data?.live_requests_1h || []).length} tone="primary" />
          <StripCard icon={Building2}  label="Active escrows"              value={(data?.active_escrows || []).length}     tone="emerald" />
          <StripCard icon={ShieldAlert} label="Risk / dispute alerts · 24h" value={(data?.alerts_24h || []).length}        tone="amber" />
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <Panel title="Country activity (last 24h)" testid="sitroom-country">
            {(data?.country_activity_24h || []).length === 0
              ? <Empty>No requests in the last 24 hours.</Empty>
              : (
                <div className="divide-y divide-border">
                  {data.country_activity_24h.map((c) => (
                    <div key={c.country || "unknown"} className="p-2.5 flex items-center justify-between">
                      <span>{c.country || "—"}</span>
                      <span className="mono text-[11px]">{c.count}</span>
                    </div>
                  ))}
                </div>
              )}
          </Panel>
          <Panel title="Asset class activity (last 24h)" testid="sitroom-assets">
            {(data?.asset_activity_24h || []).length === 0
              ? <Empty>No asset-class activity in the last 24 hours.</Empty>
              : (
                <div className="divide-y divide-border">
                  {data.asset_activity_24h.map((a) => (
                    <div key={a.asset_category} className="p-2.5 flex items-center justify-between">
                      <span className="mono text-[11px] uppercase tracking-widest">{a.asset_category || "unknown"}</span>
                      <span className="mono text-[11px]">{a.count}</span>
                    </div>
                  ))}
                </div>
              )}
          </Panel>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Panel title="High-value transactions (≥ USD 50 000)" testid="sitroom-hv">
            {(data?.high_value_transactions || []).length === 0
              ? <Empty>No high-value transactions currently.</Empty>
              : (
                <div className="divide-y divide-border">
                  {data.high_value_transactions.map((t) => (
                    <div key={t.reference || t.id} className="p-2.5">
                      <div className="mono text-[10.5px] uppercase tracking-widest text-primary">{t.reference || t.id}</div>
                      <div className="text-[12px]">{t.workflow_type || "—"} · {t.country || "—"}</div>
                      <div className="mono text-[11px] mt-0.5">USD {Number(t.amount || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
          </Panel>
          <Panel title="Risk feed (last 24h)" testid="sitroom-alerts">
            {(data?.alerts_24h || []).length === 0
              ? <Empty>No risk events flagged in the last 24 hours.</Empty>
              : (
                <div className="max-h-[420px] overflow-y-auto divide-y divide-border">
                  {data.alerts_24h.map((a, i) => (
                    <div key={i} className="p-2.5">
                      <div className="mono text-[10.5px] uppercase tracking-widest text-red-700">{a.action}</div>
                      <div className="text-[12px]">{a.target_type} · {a.target_id}</div>
                      {a.reason && <div className="text-[11.5px] text-muted-foreground">{a.reason}</div>}
                      <div className="mono text-[9.5px] uppercase text-muted-foreground mt-0.5">{(a.ts || "").slice(0, 19).replace("T", " ")}</div>
                    </div>
                  ))}
                </div>
              )}
          </Panel>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const StripCard = ({ icon: Icon, label, value, tone }) => {
  const T = { primary: "bg-primary/5 border-primary/20 text-primary",
              emerald: "bg-emerald-500/5 border-emerald-500/20 text-emerald-700",
              amber:   "bg-amber-500/5 border-amber-500/20 text-amber-700" }[tone];
  return (
    <div className={`border p-5 ${T}`}>
      <div className="flex items-center gap-2"><Icon className="h-4 w-4" /><span className="mono text-[9.5px] uppercase tracking-widest">{label}</span></div>
      <div className="text-4xl font-semibold mt-2 leading-none">{value}</div>
    </div>
  );
};

const Panel = ({ title, testid, children }) => (
  <div className="border border-border bg-card" data-testid={testid}>
    <div className="p-3 border-b border-border kicker">{title}</div>
    {children}
  </div>
);

const Empty = ({ children }) => <div className="p-6 text-sm text-muted-foreground text-center">{children}</div>;
